// lib/useDeviceType.ts
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { useMedia } from 'tamagui';

/** Shared content column width (Container, header, course toolbars). */
export const CONTENT_MAX_WIDTH = 1140;

const STABLE_WIDTH_DEBOUNCE_MS = 150;

type MediaSafe = { gtSm: boolean; short: boolean; gtMd: boolean }

/**
 * Safe wrapper around useMedia() to avoid crashes during SSR or when Tamagui isn't initialized.
 * Returns guaranteed booleans for { gtSm, short, gtMd }.
 */
function useMediaSafe(): MediaSafe {
  const m = useMedia?.() as Partial<MediaSafe> | undefined
  return { gtSm: !!m?.gtSm, short: !!m?.short, gtMd: !!m?.gtMd }
}

/**
 * useIsTabletOrWeb
 * ----------------
 * Returns true when running on:
 *  - web (treat web as "large screen"), OR
 *  - native tablet (gtSm && !short)
 *
 * Use when you want "large-screen" behaviour that includes web.
 */
export function useIsTabletOrWeb(): boolean {
  const { gtSm, short } = useMediaSafe()
  return Platform.OS === 'web' || (gtSm && !short)
}

/**
 * useIsMobile
 * -----------
 * Returns true for "mobile-like" viewports:
 *  - width < breakpoint (not gtSm), OR
 *  - short === true (mobile in landscape)
 *
 * Works the same on web and native.
 */
export function useIsMobile(): boolean {
  const { gtSm, short } = useMediaSafe()
  return !gtSm || short
}

/**
 * useIsTablet
 * -----------
 * Native-only tablet detection.
 * Returns true when running on native (not web) AND viewport is >= breakpoint AND not short.
 *
 * Use for tablet-specific native layouts.
 */
export function useIsTablet(): boolean {
  const { gtSm, short } = useMediaSafe()
  return Platform.OS !== 'web' && gtSm && !short
}

/**
 * useIsLandscapeMobile
 * --------------------
 * Returns true when the viewport is "short" (your Tamagui flag for mobile landscape).
 * If you need extra reliability on native, consider using Dimensions.get('window') as a fallback.
 *
 * Example fallback (commented):
 * // import { Dimensions } from 'react-native'
 * // const nativeLandscape = Platform.OS !== 'web' && (() => {
 * //   const { width, height } = Dimensions.get('window')
 * //   return width > height
 * // })()
 *
 * return short || nativeLandscape
 */
export function useIsLandscapeMobile(): boolean {
  const { short } = useMediaSafe()
  return short
}

/**
 * Debounced window width — use for grid/card layout during rotation only.
 * UI flags (isMobile, Resume pill, etc.) should use useIsMobile() instead.
 */
export function useStableWindowWidth(): number {
  const { width } = useWindowDimensions();
  const [stableWidth, setStableWidth] = useState(width);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (width === stableWidth) return;

    // Native rotation is a single discrete size change: reflow immediately so
    // the content tracks the new frame and never lags behind as a "stretched"
    // snapshot that snaps a moment later. Web resize can fire rapidly while the
    // user drags the window, so we keep the debounce there to avoid thrashing.
    if (Platform.OS !== 'web') {
      setStableWidth(width);
      return;
    }

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setStableWidth(width);
    }, STABLE_WIDTH_DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [width, stableWidth]);

  return stableWidth;
}

/**
 * useLessonCardLayout
 * -------------------
 * Card dimensions for LessonCard — call once at the parent/section level and
 * pass the result as props so each card doesn't need its own useMedia() call.
 *
 * Uses the debounced window width (150ms) so that rotation/resize animations
 * can complete before triggering a cascade of card re-renders.
 * Breakpoints mirror Tamagui config: gtSm ≥ 768, gtMd ≥ 1024.
 */
export function useLessonCardLayout(): {
  cardWidth: number;
  progressBarHeight: number;
  weekFormat: 'long' | 'short';
} {
  const width = useStableWindowWidth();
  const isMobile = useIsMobile();

  // Compute primitives first, then memoize on those — so the object reference
  // only changes when a value actually changes. On phones (isMobile) the result
  // is constant across rotation, so memoized sections/cards never re-render just
  // because the width number changed.
  let cardWidth: number;
  let progressBarHeight: number;
  let weekFormat: 'long' | 'short';
  if (isMobile) {
    cardWidth = 160;
    progressBarHeight = 3;
    weekFormat = 'short';
  } else {
    const gtMd = width >= 1024;
    cardWidth = gtMd ? 267 : 229;
    progressBarHeight = gtMd ? 6 : 4;
    weekFormat = 'long';
  }

  return useMemo(
    () => ({ cardWidth, progressBarHeight, weekFormat }),
    [cardWidth, progressBarHeight, weekFormat],
  );
}

/** Catalog progress bar height — matches courses hub / search rows. */
export function useCatalogProgressBarHeight(): number {
  const { gtSm, gtMd } = useMediaSafe();
  if (gtMd) return 6;
  if (gtSm) return 4;
  return 3;
}

/**
 * useAutoHideScrollbar
 * --------------------
 * Hides the scrollbar when not scrolling on web; fades it back in while scrolling.
 * Returns `scrollbarStyle` to spread into the scroll container's `style` prop and
 * `onScroll` to pass to the scroll component.
 *
 * Works with FlatList, SectionList, and ScrollView.
 * On native it is a no-op (empty style + empty handler).
 *
 * @param hideDelayMs  How long after the last scroll event the bar hides (default 700ms).
 */
export function useAutoHideScrollbar(hideDelayMs = 700): {
  scrollbarStyle: object | undefined;
  onScroll: () => void;
} {
  if (Platform.OS !== 'web') {
    return { scrollbarStyle: undefined, onScroll: () => {} };
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [visible, setVisible] = useState(false);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const onScroll = useCallback(() => {
    setVisible(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setVisible(false), hideDelayMs);
  }, [hideDelayMs]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  return {
    scrollbarStyle: { scrollbarWidth: visible ? 'thin' : 'none' } as object,
    onScroll,
  };
}
